/**
 * phiScrub — frameworkfreie PHI-/PII-Redaction für Sentry-Events.
 *
 * KANON-QUELLE. Reines TypeScript: KEINE Imports von @sentry, react-native, expo
 * oder Browser-APIs. Wird bewusst dupliziert (vendored) nach
 * `apps/care-web/src/_vendored-shared/phiScrub.ts` — beide Kopien MÜSSEN identisch
 * bleiben; Drift wird durch identische Fixtures (phiScrub.fixtures.ts) + Tests in
 * Mobile (Jest) UND care-web (Vitest) abgesichert.
 *
 * Healthcare/DSGVO Art. 9: Live-Transkripte, Wund-/Medikations-Notizen, Namen,
 * Geburtsdaten, Versicherungsnummern etc. dürfen NIE an Sentry. Das Schließen der
 * Leak-Pfade hier ist ein P0-Sicherheits-Contract, kein Komfort-Feature.
 */

export const REDACTED = "[REDACTED]";

/** Schlüssel, deren Werte komplett redacted werden (case-insensitive, substring-match). */
export const PHI_FIELDS: readonly string[] = [
  "password",
  "token",
  "access_token",
  "refresh_token",
  "email",
  "phone",
  "telefon",
  "firstname",
  "lastname",
  "first_name",
  "last_name",
  "vorname",
  "nachname",
  "dateofbirth",
  "date_of_birth",
  "geburtsdatum",
  "insurance_number",
  "versicherungsnummer",
  "ssn",
  "sozialversicherung",
  "address",
  "adresse",
  "iban",
  "credit_card",
  "kreditkarte",
  "patient_id",
  "resident_id",
  // Voice-Realtime PHI-Frei-Lock (ADR-0009 §7)
  "transcript",
  "transcription",
  "audio",
  "delta",
  "partial_transcript",
  "partialtranscript",
  "live_text",
  "cumulative",
  "pcm",
  "audio_buffer",
  "audiobuffer",
  "ephemeral_token",
  "ephemeraltoken",
  "realtime_audio",
  "voice_content",
  // Klinische Inhalte
  "wound_description",
  "medication_notes",
  "incident_notes",
  "free_note",
  "diagnosis",
  "symptoms",
];

/**
 * Tags, die KOMPLETT entfernt werden (nicht nur Wert-Redaction). Organisations-
 * Identifikatoren taugen nicht als Filter-Strategie (Codex 2026-06-03).
 */
export const DROP_TAGS: readonly string[] = [
  "practice_id",
  "facility_id",
  "practiceid",
  "facilityid",
];

const MAX_DEPTH = 8;

function keyIsPhi(key: string): boolean {
  const lower = key.toLowerCase();
  return PHI_FIELDS.some((field) => lower.includes(field));
}

/**
 * Redaction für Freitext (Error-Messages, Exception-Werte): E-Mails, Bearer-/lange
 * Tokens, UUIDs, IBAN-artige Strings. Heuristisch — bewusst best-effort; die harte
 * Linie ist die schlüsselbasierte/strukturelle Redaction unten.
 */
export function scrubString(input: string): string {
  if (!input) return input;
  return input
    // E-Mail
    .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, REDACTED)
    // Bearer-Token
    .replace(/\bBearer\s+[A-Za-z0-9._-]+/gi, `Bearer ${REDACTED}`)
    // JWT (drei base64url-Segmente)
    .replace(/\b[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g, REDACTED)
    // UUID
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, REDACTED)
    // IBAN
    .replace(/\b[A-Z]{2}\d{2}[A-Z0-9]{11,30}\b/g, REDACTED)
    // lange opake Token / Hashes / Base64 (>= 20 zusammenhängende Token-Zeichen)
    .replace(/[A-Za-z0-9_-]{20,}/g, REDACTED);
}

/**
 * URL säubern: Query-String entfernen, tokenartige Pfadsegmente
 * (`/admission/:token`, Magic-Link) maskieren. Funktioniert ohne URL-API.
 */
export function scrubUrl(input: string): string {
  if (!input) return input;
  const [pathPart = ""] = input.split("?");
  const segments = pathPart.split("/").map((seg) => {
    if (!seg) return seg;
    // tokenartig: lang/opak, UUID, oder reine Hex/Base64
    if (
      seg.length >= 16 ||
      /^[0-9a-f]{8}-/i.test(seg) ||
      /^[A-Za-z0-9_-]{12,}$/.test(seg)
    ) {
      return REDACTED;
    }
    return seg;
  });
  return segments.join("/");
}

/** Rekursive, schlüsselbasierte Redaction für beliebige Objekte. */
export function scrubObject(
  obj: Record<string, unknown>,
  depth = 0,
): Record<string, unknown> {
  if (depth > MAX_DEPTH || obj === null || typeof obj !== "object") return obj;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (keyIsPhi(key)) {
      out[key] = REDACTED;
    } else if (Array.isArray(value)) {
      out[key] = value.map((v) => {
        if (v && typeof v === "object") return scrubObject(v as Record<string, unknown>, depth + 1);
        return typeof v === "string" ? scrubString(v) : v;
      });
    } else if (value && typeof value === "object") {
      out[key] = scrubObject(value as Record<string, unknown>, depth + 1);
    } else if (typeof value === "string") {
      // String-Werte (z. B. eine `url` in breadcrumb.data) können strukturiertes
      // PHI tragen, auch wenn der Schlüssel selbst unauffällig ist.
      out[key] = scrubString(value);
    } else {
      out[key] = value;
    }
  }
  return out;
}

// ---- Strukturelle Sentry-Event-Form (KEIN @sentry-Import) --------------------

interface ScrubFrame {
  vars?: Record<string, unknown>;
  [k: string]: unknown;
}
interface ScrubException {
  value?: string;
  stacktrace?: { frames?: ScrubFrame[] };
  [k: string]: unknown;
}
interface ScrubBreadcrumb {
  message?: string;
  category?: string;
  data?: Record<string, unknown>;
  [k: string]: unknown;
}
export interface ScrubbableEvent {
  message?: string;
  transaction?: string;
  exception?: { values?: ScrubException[] };
  request?: {
    url?: string;
    query_string?: unknown;
    data?: unknown;
    cookies?: unknown;
    headers?: unknown;
    [k: string]: unknown;
  };
  tags?: Record<string, unknown>;
  extra?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
  breadcrumbs?: ScrubBreadcrumb[];
  user?: { id?: string; email?: string; username?: string; ip_address?: string; [k: string]: unknown };
  [k: string]: unknown;
}

/**
 * Vollständige Event-Redaction. Deckt alle Leak-Pfade ab, die die alte
 * `scrubPII` übersah: message, exception-Werte, Stacktrace-Frame-vars,
 * transaction (Route), request (URL/query/data/cookies/headers), tags.
 *
 * Hinweis: mutiert das Event in-place (Sentry-`beforeSend`-Idiom) und gibt es
 * zurück. Bewusste Ausnahme von der allg. Immutabilitätsregel — `beforeSend`
 * erwartet genau das.
 */
export function scrubEvent<T extends ScrubbableEvent>(event: T): T {
  if (!event || typeof event !== "object") return event;

  if (typeof event.message === "string") {
    event.message = scrubString(event.message);
  }
  if (typeof event.transaction === "string") {
    event.transaction = scrubUrl(event.transaction);
  }

  if (event.exception?.values) {
    for (const ex of event.exception.values) {
      if (typeof ex.value === "string") ex.value = scrubString(ex.value);
      if (ex.stacktrace?.frames) {
        for (const frame of ex.stacktrace.frames) {
          // Lokale Variablen können beliebige PHI-haltige Werte enthalten.
          if (frame.vars) delete frame.vars;
        }
      }
    }
  }

  if (event.request) {
    if (typeof event.request.url === "string") {
      event.request.url = scrubUrl(event.request.url);
    }
    delete event.request.query_string;
    delete event.request.data;
    delete event.request.cookies;
    delete event.request.headers;
  }

  if (event.tags) {
    for (const tag of Object.keys(event.tags)) {
      if (DROP_TAGS.includes(tag.toLowerCase())) delete event.tags[tag];
    }
    event.tags = scrubObject(event.tags) as Record<string, unknown>;
  }

  if (event.extra) {
    event.extra = scrubObject(event.extra);
  }

  if (event.contexts) {
    for (const key of Object.keys(event.contexts)) {
      const ctx = event.contexts[key];
      if (ctx && typeof ctx === "object") {
        event.contexts[key] = scrubObject(ctx as Record<string, unknown>);
      }
    }
  }

  if (event.breadcrumbs) {
    for (const crumb of event.breadcrumbs) {
      if (typeof crumb.message === "string") crumb.message = scrubString(crumb.message);
      if (crumb.data) crumb.data = scrubObject(crumb.data);
    }
  }

  if (event.user) {
    delete event.user.email;
    delete event.user.username;
    delete event.user.ip_address;
  }

  return event;
}
